// src/cashup/useOrgBranding.js
import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

const defaults = {
  primary:"#00E1A7", accent:"#4DD0E1", bg:"#0b0f19", surface:"#111a2b", text:"#e8edf7"
};

const BRANDING_CACHE_KEY = "orgBranding";

function buildBrandingCache(data = {}) {
  return {
    brand: data.brand || {},
    logo: data.logo || { lightUrl: "", darkUrl: "" },
    faviconUrl: data.faviconUrl || "",
    splash: data.splash || { enabled: false, durationMs: 0 },
  };
}

function safeSetBrandingCache(data) {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(buildBrandingCache(data)));
  } catch (err) {
    console.warn("Org branding cache skipped:", err?.message || err);
    try {
      localStorage.removeItem(BRANDING_CACHE_KEY);
    } catch (_) {}
  }
}

function safeGetBrandingCache() {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (err) {
    console.warn("Org branding cache read skipped:", err?.message || err);
    try {
      localStorage.removeItem(BRANDING_CACHE_KEY);
    } catch (_) {}
    return null;
  }
}

export default function useOrgBranding(orgId) {
  const [config, setConfig] = useState(null);

  const hexToRgba = (hex, alpha = 1) => {
    try {
      const h = String(hex || '').trim().replace('#', '');
      if (![3, 6].includes(h.length)) return null;
      const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      if ([r, g, b].some((n) => Number.isNaN(n))) return null;
      const a = Math.max(0, Math.min(1, Number(alpha)));
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!orgId) return;
    // C8.8 — Branding is presentation/configuration, not operational state.
    // Load once for this mounted admin session; Branding writes can be seen on next mount/refresh.
    getDoc(doc(db, "orgs", orgId)).then((snap) => {
      const data = snap.data() || {};
      setConfig(data);

      const b = { ...defaults, ...(data.brand || {}) };
      const root = document.documentElement.style;
      root.setProperty("--brand-primary", b.primary);
      root.setProperty("--brand-accent",  b.accent);
      root.setProperty("--brand-bg",      b.bg);
      root.setProperty("--brand-surface", b.surface);
      root.setProperty("--brand-text",    b.text);

      // Derived helpers used by Overview cards/tables so they track Accent changes.
      root.setProperty("--accent-bg", hexToRgba(b.accent, 0.14) || "rgba(77,208,225,0.14)");
      root.setProperty("--accent-bg-strong", hexToRgba(b.accent, 0.22) || "rgba(77,208,225,0.22)");
      root.setProperty("--accent-border", hexToRgba(b.accent, 0.28) || "rgba(77,208,225,0.28)");
      root.setProperty("--accent-border-strong", hexToRgba(b.accent, 0.48) || "rgba(77,208,225,0.48)");

      if (data.faviconUrl) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href = data.faviconUrl;
      }

      safeSetBrandingCache(data);
    }).catch(() => {
      const cached = safeGetBrandingCache();
      if (cached) setConfig(cached);
    });
  }, [orgId]);

  // offline fallback
  useEffect(() => {
    if (config) return;
    const cached = safeGetBrandingCache();
    if (cached) setConfig(cached);
  }, [config]);

  return {
    brand:  config?.brand || defaults,
    logo:   config?.logo  || { lightUrl: "", darkUrl: "" },
    splash: config?.splash || { enabled: false, durationMs: 0 },
    faviconUrl: config?.faviconUrl || "",
  };
}
